import { useId, useState } from "react";
import { Link } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { axiosInstance } from "../../axios";
import "../../styles/Footer.css";

const HOMEOWNER_LINKS = [
  { to: "/chat", label: "Get a Free Estimate" },
  { to: "/quote-check", label: "Check a Quote" },
  { to: "/estimators", label: "AI Estimators" },
  { to: "/cost-guides", label: "Cost Guides" },
  { to: "/local-contractors", label: "Find Local Contractors" },
  { to: "/cost-calculator", label: "Cost Calculator" },
  { to: "/warm-homes-plan", label: "Grant Checker" },
  { to: "/your-home", label: "Your Home Dashboard" },
  { to: "/homeowner-trust", label: "Hire With Confidence" },
  { to: "/signup/homeowner", label: "Sign Up Free", guestOnly: true },
  { to: "/newsletter", label: "Our Newsletter" },
];

const TRADE_LINKS = [
  { to: "/for-tradespeople", label: "Get More Trade Leads" },
  { to: "/widget-installation", label: "AI Website Widget" },
  { to: "/signup/contractor", label: "Join as a Tradesperson", guestOnly: true },
  { to: "/contractor-packages", label: "Packages and Prices" },
  { to: "/trade-advice", label: "Trade Business Advice" },
  { to: "/day-rates", label: "Trade Day Rates" },
  { to: "/trust-score", label: "Get the Trusted by Percy Badge" },
  { to: "/free-resources", label: "Free Resources" },
  { to: "/trades-newsletter", label: "The Trade Dispatch" },
];

const POPULAR_GUIDES = [
  { to: "/cost-guides/tiling", label: "Tiling Costs Per m²" },
  { to: "/cost-guides/flooring", label: "Flooring Costs" },
  { to: "/cost-guides/carpentry", label: "Carpentry Costs" },
  { to: "/cost-guides/kitchen-renovation", label: "Kitchen Renovation Costs" },
  { to: "/cost-guides/bathroom-renovation", label: "Bathroom Renovation Costs" },
  { to: "/cost-guides/driveway", label: "Driveway Costs" },
  { to: "/cost-guides/conservatory", label: "Conservatory Prices" },
  { to: "/cost-guides/damp-proofing", label: "Damp Proofing Costs" },
  { to: "/cost-guides", label: "All Cost Guides", strong: true },
  { to: "/trade-advice", label: "All Trade Advice", strong: true },
];

const COMPANY_LINKS = [
  { to: "/about", label: "About Pricing Penguin" },
  { to: "/blog", label: "Read Our Blog" },
  { to: "/reports/penguin-index", label: "The Penguin Index" },
  { to: "/reports/uk-home-improvement-costs-2026", label: "2026 Cost Report" },
  { to: "/apps", label: "Get the App" },
  { to: "/partners", label: "Partner Programme" },
  { to: "/careers", label: "Careers" },
  { to: "/sustainability", label: "Sustainability" },
];

const HELP_LINKS = [
  { to: "/faqs", label: "FAQs" },
  { to: "/contact", label: "Contact Us" },
  { to: "/support", label: "Customer Support" },
  { to: "/trusted-by-percy", label: "Trusted by Percy" },
  { to: "/verify", label: "Check a Percy Badge" },
  { to: "/policies", label: "Policies" },
];

const LINK_CLASS = "block py-1.5 md:py-1 hover:text-amber-600";

const FooterColumn = ({ heading, links, isAuthenticated, children }) => (
  <nav aria-label={heading} className="min-w-0">
    <h3 className="font-semibold text-lg mb-3">{heading}</h3>
    <ul className="text-sm">
      {links
        .filter((link) => !(link.guestOnly && isAuthenticated))
        .map((link) => (
          <li key={`${link.to}-${link.label}`}>
            <Link to={link.to} className={`${LINK_CLASS}${link.strong ? " font-semibold" : ""}`}>
              {link.label}
            </Link>
          </li>
        ))}
      {children}
    </ul>
  </nav>
);

const Footer = () => {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const isContractor = isAuthenticated && user.user_type === "contractor";
  const [email, setEmail] = useState("");
  const emailId = useId();
  const [consent, setConsent] = useState(false);
  // null | "sending" | "pending" | "done" | "error"
  const [subscribeStatus, setSubscribeStatus] = useState(null);
  const [subscribeMessage, setSubscribeMessage] = useState("");

  // Contractors get the Trade Dispatch; everyone else the homeowner edition.
  const newsletterEdition = isContractor ? "trades" : "homeowner";

  // PECR: the exact consent wording shown here is stored server-side as
  // the consent record — the label below RENDERS this same string (minus
  // the version tag), so display and stored record can never drift.
  const FOOTER_CONSENT_TEXT = isContractor
    ? "Yes — email me the Pricing Penguin Trade Dispatch (roughly " +
      "fortnightly). I can unsubscribe any time. [v1]"
    : "Yes — email me the Pricing Penguin newsletter (roughly " +
      "fortnightly). I can unsubscribe any time. [v1]";

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !consent || subscribeStatus === "sending") return;
    setSubscribeStatus("sending");
    try {
      const res = await axiosInstance.post("/newsletter/subscribe/", {
        email: trimmed,
        edition: newsletterEdition,
        consent: true,
        consent_source: "footer",
        consent_text: FOOTER_CONSENT_TEXT,
      });
      // Same contract as NewsletterSignupForm: show the API's own message
      // and honour pending_confirmation (double opt-in) vs already-subscribed.
      setSubscribeMessage(res.data?.message || "Thanks — check your inbox!");
      setSubscribeStatus(res.data?.pending_confirmation ? "pending" : "done");
      setEmail("");
      setConsent(false);
    } catch (err) {
      setSubscribeMessage(
        err.response?.data?.error ||
          "Something went wrong — please try again in a minute."
      );
      setSubscribeStatus("error");
    }
  };

  // Get the version directly from the environment variable
  const appVersion = process.env.REACT_APP_VERSION;

  return (
    <footer
      id="footer"
      className="footer-container bg-white border-t border-zinc-200 py-10 text-zinc-600 mt-8"
    >
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-8">
        <FooterColumn heading="Homeowners" links={HOMEOWNER_LINKS} isAuthenticated={isAuthenticated} />
        <FooterColumn heading="Tradespeople" links={TRADE_LINKS} isAuthenticated={isAuthenticated} />
        <FooterColumn heading="Popular Guides" links={POPULAR_GUIDES} isAuthenticated={isAuthenticated} />
        <FooterColumn heading="Company" links={COMPANY_LINKS} isAuthenticated={isAuthenticated} />
        <FooterColumn heading="Help" links={HELP_LINKS} isAuthenticated={isAuthenticated}>
          <li>
            {/* Reopens the consent banner — required so visitors can change
                their cookie choice at any time (ICO). */}
            <button
              type="button"
              className={`${LINK_CLASS} cursor-pointer bg-transparent border-0 px-0 text-inherit font-inherit text-left`}
              onClick={() =>
                window.dispatchEvent(new CustomEvent("pp:open-cookie-settings"))
              }
            >
              Cookie Settings
            </button>
          </li>
        </FooterColumn>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-10 pt-8 border-t border-zinc-200 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <div className="min-w-0 max-w-md">
          <h3 className="font-semibold text-lg mb-3">Stay Connected</h3>
          <p className="text-sm mb-2">
            {isContractor
              ? "Pricing insight for the trade, in your inbox."
              : "Get home improvement tips and cost insights in your inbox."}
          </p>
          {subscribeStatus === "pending" || subscribeStatus === "done" ? (
            <p className="footer-nl-status text-sm mb-3" role="status">
              {subscribeMessage}
            </p>
          ) : (
            <form onSubmit={handleSubscribe}>
              <label htmlFor={emailId} className="block text-sm font-medium mb-1">Email address</label>
              <div className="footer-email-form">
                <input
                  id={emailId}
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="footer-email-input"
                />
                <button
                  type="submit"
                  className="footer-email-btn"
                  disabled={subscribeStatus === "sending"}
                >
                  {subscribeStatus === "sending" ? "Sending…" : "Subscribe"}
                </button>
              </div>
              <label className="flex items-start gap-2 text-xs text-zinc-500 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  required
                  className="mt-0.5 accent-amber-500"
                />
                <span>
                  {FOOTER_CONSENT_TEXT.replace(" [v1]", "")}{" "}
                  <Link to="/policies" className="underline hover:text-amber-600">
                    Privacy
                  </Link>
                </span>
              </label>
              {subscribeStatus === "error" && (
                <p className="footer-nl-error text-xs" role="alert">
                  {subscribeMessage}
                </p>
              )}
            </form>
          )}
        </div>
        <div className="flex space-x-5 md:justify-end md:pt-10">
          <a
            href="https://www.facebook.com/share/1BrB4CMMgu/?mibextid=wwXIfr"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Pricing Penguin on Facebook"
          >
            <i className="fab fa-facebook text-2xl text-zinc-400 hover:text-amber-600" aria-hidden="true"></i>
          </a>
          <a
            href="https://www.instagram.com/pricingpenguin?igsh=MXZwdnZxaDIwZG5nYQ%3D%3D&utm_source=qr"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Pricing Penguin on Instagram"
          >
            <i className="fab fa-instagram text-2xl text-zinc-400 hover:text-amber-600" aria-hidden="true"></i>
          </a>
          <a
            href="https://www.linkedin.com/company/pricing-penguin-ltd/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Pricing Penguin on LinkedIn"
          >
            <i className="fab fa-linkedin text-2xl text-zinc-400 hover:text-amber-600" aria-hidden="true"></i>
          </a>
          <a
            href="https://www.tiktok.com/@pricingpenguin"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Pricing Penguin on TikTok"
          >
            <i className="fab fa-tiktok text-2xl text-zinc-400 hover:text-amber-600" aria-hidden="true"></i>
          </a>
        </div>
      </div>
      <div className="text-center text-sm mt-8 text-zinc-500">
        <p>
          © {new Date().getFullYear()} Pricing Penguin. All Rights Reserved.
        </p>
        <p className="text-xs">
          Your trusted AI-powered price estimation service for home improvement.
        </p>
        {appVersion && (
          <p className="text-xs mt-2 text-zinc-400">
            Version: {appVersion}
          </p>
        )}
      </div>
    </footer>
  );
};

export default Footer;
